import { Alert, Badge, Button } from 'react-bootstrap';

import { RUN_UNFINISHED_MESSAGE, runErrorMessage } from './incidentsErrorMessage';
import { runOutcomeNotice } from './runOutcomeNotice';
import type { WatchedRunNotice } from './useDetectionRun';

const MS_PER_MINUTE = 60 * 1000;

const minutesBetween = (since: string, now: Date): number =>
  Math.floor((now.getTime() - new Date(since).getTime()) / MS_PER_MINUTE);

type RunDetectionNoticeProps = {
  notice: WatchedRunNotice | undefined;
  now: Date;
  onRunAnyway: () => void;
};

function ForcedRunLabel({ forced }: { forced: boolean }) {
  if (!forced) {
    return null;
  }

  return (
    <Badge bg="secondary" className="me-2">
      Forced run
    </Badge>
  );
}

function RunDetectionNotice({ notice, now, onRunAnyway }: RunDetectionNoticeProps) {
  if (notice === undefined) {
    return null;
  }

  if (notice.kind === 'refused') {
    return <Alert variant="danger">{runErrorMessage(notice.error)}</Alert>;
  }

  if (notice.kind === 'unfinished') {
    return (
      <Alert variant="danger">
        <ForcedRunLabel forced={notice.forced} />
        {RUN_UNFINISHED_MESSAGE}
      </Alert>
    );
  }

  if (notice.kind === 'overrunning') {
    const minutesAgo = minutesBetween(notice.startedAt, now);

    return (
      <Alert variant="warning" role="status">
        <ForcedRunLabel forced={notice.forced} />
        The detection run is taking longer than usual — still working (started {minutesAgo} min ago)
      </Alert>
    );
  }

  const outcome = runOutcomeNotice(notice.report, now);
  const offersRunAnyway = notice.report.outcome === 'no-new-data' && !notice.forced;

  return (
    <Alert variant={outcome.variant} role="status">
      <ForcedRunLabel forced={notice.forced} />
      {outcome.line}
      {offersRunAnyway && (
        <Button variant="outline-secondary" size="sm" className="ms-2" onClick={onRunAnyway}>
          Run anyway
        </Button>
      )}
    </Alert>
  );
}

export default RunDetectionNotice;
